import type { StepConfig } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import useStepByStepFlowState from "~utils/use-step-by-step-flow-state"
import type {
    CheckoutQueryParams,
    CheckoutState,
    UpdateCheckoutQueryParamsFunc,
} from "~utils/use-checkout-query-params"

export type GoToStepArgs = Partial<CheckoutQueryParams> & {
    step: string | null
}
export type GoToNextPrevArgs = Omit<GoToStepArgs, "step">

export default function useCheckoutStepByStepFlowState({
    stepConfigs,
    checkoutState,
    updateCheckoutQueryParams,
}: {
    stepConfigs: StepConfig[]
    checkoutState: CheckoutState
    updateCheckoutQueryParams: UpdateCheckoutQueryParamsFunc
}) {
    const {
        firstStep,
        nextStep,
        previousStep,
        hasNextStep,
        hasPreviousStep,
        ...flowState
    } = useStepByStepFlowState({ stepConfigs })

    const goToStep = (args: GoToStepArgs) =>
        updateCheckoutQueryParams({ checkout_state: checkoutState, ...args })

    const goToNextStep = (params?: GoToNextPrevArgs) => {
        if (!hasNextStep) return

        goToStep({ ...params, step: nextStep })
    }

    const goToPreviousStep = (params?: GoToNextPrevArgs) => {
        if (!hasPreviousStep) return

        goToStep({ ...params, step: previousStep })
    }

    const goToFirstStep = (params?: GoToNextPrevArgs) => {
        if (!firstStep) return

        goToStep({ ...params, step: firstStep })
    }

    return {
        ...flowState,
        goToStep,
        goToNextStep,
        goToPreviousStep,
        goToFirstStep,
        hasNextStep,
        hasPreviousStep,
    }
}
